import { formatUnitValue } from "../unitWiseCostFilterOptions";
import { generateUnitColors } from "./unitWiseCostDoughnutChart";
/* -------------------------------------------------------------------------- */
/*                                CHART DATA                                  */
/* -------------------------------------------------------------------------- */

// rows: [{ projectKey, ioNo, projectName, unitCode, unitName, value }, ...]
// already sorted descending by value.
export const getProjectWiseCostChartData = (rows, valueMode) => {

    const colors = generateUnitColors(rows.length);

    return {
        labels: rows.map((row) => row.ioNo),
        datasets: [
            {
                label: "Value",
                data: rows.map((row) => row.value),
                // Carried on the dataset (Chart.js ignores keys it doesn't
                // recognise) so the value-label plugin can read fresh
                // per-bar info straight off `chart.data` instead of closing
                // over `rows`/`valueMode` - react-chartjs-2 reliably re-diffs
                // `data`/`options` on every render but NOT the `plugins`
                // array, so a plugin capturing external values by closure
                // goes stale (still drawing the *previous* render's rows)
                // whenever the dataset changes without the component
                // remounting - e.g. a project dropping out on FY change
                // shifted every row below it up one slot, but the plugin
                // kept painting the old row's unit name/value at that slot.
                rowMeta: rows.map((row) => ({
                    projectName: row.projectName,
                })),
                valueMode,
                backgroundColor: colors,
                hoverBackgroundColor: colors,
                borderRadius: {
                    topLeft: 0,
                    bottomLeft: 0,
                    topRight: 10,
                    bottomRight: 10,
                },
                barThickness: 22,
                maxBarThickness: 26,
            },
        ],
    };
};

/* -------------------------------------------------------------------------- */
/*                                X AXIS RANGE                                */
/* -------------------------------------------------------------------------- */

// A "nice" round upper bound (1/2/5/10 x a power of ten) above the largest
// bar value, so the axis header (no data of its own) and the scrolling body
// chart can be given the exact same min/max/stepSize and always agree on
// where each percentage/crore mark falls.
const getNiceAxisMax = (maxValue) => {
    if (!maxValue || maxValue <= 0) return 10;

    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const normalized = maxValue / magnitude;

    let niceNormalized = 10;
    if (normalized <= 1) niceNormalized = 1;
    else if (normalized <= 2) niceNormalized = 2;
    else if (normalized <= 5) niceNormalized = 5;

    return niceNormalized * magnitude;
};

export const getProjectWiseCostXAxisMax = (rows) => {
    const maxValue = rows.reduce((max, row) => Math.max(max, row.value || 0), 0);
    return getNiceAxisMax(maxValue);
};

// Fixed number of partitions (0%, 20%, 40%... / ₹0, ₹100, ₹200...) instead
// of leaving it to Chart.js's default "nice number" tick algorithm, which -
// given an unusual max like 500 with no explicit step - can generate dozens
// of closely-packed ticks that overlap into unreadable noise, especially in
// the axis header's short strip.
const AXIS_PARTITION_COUNT = 5;

export const getProjectWiseCostXAxisStepSize = (rows) =>
    getProjectWiseCostXAxisMax(rows) / AXIS_PARTITION_COUNT;

// Room reserved past the end of the longest bar for the value-label plugin's
// "88.00%" text. Shared with the CSS axis header below so both always agree
// on where the plottable area ends.
export const PROJECT_WISE_COST_RIGHT_PADDING_PX = 80;

/* -------------------------------------------------------------------------- */
/*                               CHART OPTIONS                                */
/* -------------------------------------------------------------------------- */

export const getProjectWiseCostChartOptions = (valueMode, rows) => {

    const xAxisMax = getProjectWiseCostXAxisMax(rows);
    const xAxisStepSize = getProjectWiseCostXAxisStepSize(rows);

    return {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            // extra right padding so the "88.00%" value-label plugin has
            // room to draw past the end of the longest bar.
            padding: { right: PROJECT_WISE_COST_RIGHT_PADDING_PX },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items) => {
                        const row = rows[items[0]?.dataIndex];
                        if (!row) return "";
                        return `${row.ioNo} - ${row.projectName}`;
                    },
                    label: (ctx) => {
                        const row = rows[ctx.dataIndex];
                        if (!row) return "";
                        return `${row.unitName}: ${formatUnitValue(row.labelValue, valueMode)}`;
                    },
                },
            },
            // chartjs-plugin-datalabels is registered globally (see
            // BarChart.jsx/StackedBarLineChart.jsx) so it applies to every
            // chart by default - this chart already draws its own value
            // label outside the bar via projectWiseCostValueLabelPlugin
            // below, so the plugin's default in-bar label must stay off.
            datalabels: {
                display: false,
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                min: 0,
                max: xAxisMax,
                // Ticks are drawn by the sticky axis header instead - the
                // scrolling body only needs to keep the (darker) partition
                // gridlines as a visual reference behind the bars. Same
                // fixed stepSize as the header so the two line up.
                ticks: { display: false, stepSize: xAxisStepSize },
                grid: { color: "#98A2B3" },
            },
            y: {
                // IO numbers removed from the axis - the project name is
                // now drawn inside each bar instead (see the value-label
                // plugin below).
                display: false,
            },
        },
    };
};

/* -------------------------------------------------------------------------- */
/*                    STICKY X AXIS HEADER (plain CSS, no canvas)             */
/* -------------------------------------------------------------------------- */

// The header is plain HTML/CSS (not a second Chart.js canvas) rendered in a
// `position: sticky` wrapper above the scrolling bars, so the percentage/
// crore scale stays visible no matter how far the user scrolls. A canvas
// with an empty dataset can't reliably size its own plot area, which was
// letting the header chart report a wider-than-container layout and bleed
// past the card's edges - a plain flex row of labels can't do that.
//
// `AXIS_PARTITION_COUNT + 1` evenly-spaced values (0, step, 2*step, ..., max)
// rendered with `justify-content: space-between` inside a box matching the
// body chart's right padding land at the same relative positions as the
// evenly-spaced (same stepSize) gridlines drawn on the body canvas.
export const getProjectWiseCostXAxisTicks = (rows) => {
    const xAxisMax = getProjectWiseCostXAxisMax(rows);
    const xAxisStepSize = getProjectWiseCostXAxisStepSize(rows);
    return Array.from(
        { length: AXIS_PARTITION_COUNT + 1 },
        (_, index) => xAxisStepSize * index
    );
};

/* -------------------------------------------------------------------------- */
/*                                  SIZING                                    */
/* -------------------------------------------------------------------------- */

// Horizontal bars: height grows with row count (unlike the vertical chart,
// which scales width). Caller wraps this in a scrollable container if it
// exceeds a reasonable max.
export const getProjectWiseCostChartHeight = (rowCount) => {
    return Math.max(320, rowCount * 44 + 60);
};

/* -------------------------------------------------------------------------- */
/*                      "PROJECT NAME  XX%" DATA LABEL                        */
/* -------------------------------------------------------------------------- */

// Plain plugin object (no factory args) - it reads `rowMeta`/`valueMode`
// straight off the live dataset (see getProjectWiseCostChartData above) on
// every draw, so it can never go stale relative to a captured closure.
export const projectWiseCostValueLabelPlugin = {
    id: "projectWiseCostValueLabelPlugin",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const dataset = chart.data.datasets[0];
        const meta = chart.getDatasetMeta(0);

        if (!dataset) return;

        meta.data.forEach((bar, index) => {
            const rowMeta = dataset.rowMeta?.[index];
            if (!rowMeta || !bar) return;

            const valueLabel = formatUnitValue(dataset.data[index], dataset.valueMode);

            // Project name inside the bar. Clipped to the bar's own
            // rectangle so a long name never bleeds past its end onto the
            // white plot background, where white text would disappear.
            // A dark outline behind the white fill keeps it legible against
            // every generated bar hue, not just the darker ones.
            ctx.save();
            ctx.beginPath();
            ctx.rect(bar.base, bar.y - bar.height / 2, bar.x - bar.base, bar.height);
            ctx.clip();
            ctx.font = "600 12px Inter, sans-serif";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.lineWidth = 3;
            ctx.strokeStyle = "rgba(16, 24, 40, 0.55)";
            ctx.strokeText(rowMeta.projectName, bar.base + 8, bar.y);
            ctx.fillStyle = "#FFFFFF";
            ctx.fillText(rowMeta.projectName, bar.base + 8, bar.y);
            ctx.restore();

            // Value stays past the bar's end, on the white background.
            ctx.save();
            ctx.fillStyle = "#101828";
            ctx.font = "600 12px Inter, sans-serif";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(valueLabel, bar.x + 8, bar.y);
            ctx.restore();
        });
    },
};